import { makeAccountInput } from '@/modules/entities/EntitySubforms/utils/shared/accounts.utils';
import { trustTaxStatusToApiTypeMap } from '@/modules/entities/EntitySubforms/utils/shared/taxStatus.utils';
import { getTaxStatusValuesFromEntityType } from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AugmentedCreateCltTrustInput,
  AugmentedCreateCrtTrustInput,
  AugmentedCreateEntityInput,
  AugmentedCreateIlitTrustInput,
  AugmentedCreateIrrevocableTrustInput,
  AugmentedCreateQprtTrustInput,
  AugmentedCreateRevocableTrustInput,
  AugmentedCreateSlatTrustInput,
} from '@/types/schema';

import {
  CREATE_ENTITY_SHORT_FORM_NAMESPACE,
  EntityShortFormShape,
} from '../CreateEntityShortForm.types';
import {
  getCreateEntityInputCommonProps,
  getCreateSubtypeInputCommonProps,
  getSinglePrincipalInput,
  MakeCreateEntityInputParams,
  makeCreateMultiplePrincipalInputs,
} from './CreateEntityShortForm.common';

export function makeCreateRevocableTrustInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withRevocableTrust: makeCreateTrustInput(formInput, householdId),
  };

  return input;
}

export function makeCreateIrrevocableTrustInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withIrrevocableTrust: makeCreateTrustInput(formInput, householdId),
  };

  return input;
}

export function makeCreateIlitTrustInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withIlitTrust: makeCreateTrustInput(formInput, householdId),
  };

  return input;
}

export function makeCreateSlatTrustInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: getCreateEntityInputCommonProps(formInput, householdId, params),
    withSlatTrust: makeCreateTrustInput(formInput, householdId),
  };

  return input;
}

export function makeCreateQprtTrustInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: {
      ...getCreateEntityInputCommonProps(formInput, householdId, params),
    },
    withQprtTrust: makeCreateTrustWithRequiredHouseholdIDInput(
      formInput,
      householdId
    ),
  };

  return input;
}

export function makeCreateCltInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: {
      ...getCreateEntityInputCommonProps(formInput, householdId, params),
    },
    withCltTrust: makeCreateTrustWithRequiredHouseholdIDInput(
      formInput,
      householdId
    ),
  };

  return input;
}

export function makeCreateCrtInput(
  formInput: EntityShortFormShape,
  householdId: string,
  params: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input: AugmentedCreateEntityInput = {
    create: {
      ...getCreateEntityInputCommonProps(formInput, householdId, params),
    },
    withCrtTrust: makeCreateTrustWithRequiredHouseholdIDInput(
      formInput,
      householdId
    ),
  };

  return input;
}

type AugmentedCreateTrustCombinedType =
  | AugmentedCreateRevocableTrustInput
  | AugmentedCreateIrrevocableTrustInput
  | AugmentedCreateIlitTrustInput
  | AugmentedCreateSlatTrustInput;

type AugmentedCreateTrustCombinedWithRequiredHouseholdIDType =
  | AugmentedCreateQprtTrustInput
  | AugmentedCreateCrtTrustInput
  | AugmentedCreateCltTrustInput;

function isTrustInputWithRequiredHouseholdID(
  _input: AugmentedCreateTrustCombinedType,
  entityType: EntityType | ''
): _input is AugmentedCreateTrustCombinedWithRequiredHouseholdIDType {
  return entityType === 'qprt' || entityType === 'clt' || entityType === 'crt';
}

// this is a wrapper around makeCreateTrustInput that satisfies the TS requirement that the householdID
// is always present.
function makeCreateTrustWithRequiredHouseholdIDInput(
  formInput: EntityShortFormShape,
  householdID: string
): AugmentedCreateTrustCombinedWithRequiredHouseholdIDType {
  if (!householdID) {
    throw new Error('Household ID is required');
  }

  const output = makeCreateTrustInput(formInput, householdID);
  const entityType = formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType;

  if (!isTrustInputWithRequiredHouseholdID(output, entityType)) {
    throw new Error(
      `Entity type ${entityType} does not support required household ID`
    );
  }

  output.create.householdID = householdID;

  return output;
}

function makeCreateTrustInput(
  formInput: EntityShortFormShape,
  householdId: string
): AugmentedCreateTrustCombinedType {
  const grantorProperties: Partial<AugmentedCreateTrustCombinedType> = (() => {
    if (formInput.createEntityShortForm.entityType === 'slat') {
      return {
        withGrantor: getSinglePrincipalInput(formInput, householdId),
      };
    }

    return {
      withGrantors: makeCreateMultiplePrincipalInputs(formInput, householdId),
    };
  })();

  const taxStatusFormDefaultValues = getTaxStatusValuesFromEntityType(
    formInput[CREATE_ENTITY_SHORT_FORM_NAMESPACE].entityType as EntityType
  );
  const input: AugmentedCreateTrustCombinedType = {
    create: {
      ...getCreateSubtypeInputCommonProps(formInput, householdId),
      taxStatus: taxStatusFormDefaultValues.taxStatus
        ? trustTaxStatusToApiTypeMap[taxStatusFormDefaultValues.taxStatus]
        : null,
    },
    ...grantorProperties,
    withDesignerAccount: makeAccountInput(formInput, householdId),
  };

  return input;
}
